import { jsxs, jsx } from "react/jsx-runtime";
import { Paragraph, Box, TextButton } from "@zillow/constellation-10";
import { event } from "@zillow/universal-analytics";
import { yieldCallback } from "@zillow/yield-callback";
import { debounce } from "lodash";
import React, { useState, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
import LegalPopover from "../CalculatorForm/LegalPopover.esm.js";
import CheckboxField from "../Fields/CheckboxField.esm.js";
import CondtionalField from "../Fields/ConditionalField.esm.js";
import TextField from "../Fields/TextField.esm.js";
import { FieldConfig } from "./AffordabilityCalculator.utils.esm.js";
import { trackLabel } from "./trackLabel.esm.js";
const handleFieldChange = debounce((label) => {
  const cmsInfo = {
    block_type_cd: "form_field",
    block_id: "afffordability_calc",
    block_displayed_txt: label
  };
  sendInteractionEvent(cmsInfo);
}, 3e3);
const sendInteractionEvent = (cmsInfo) => {
  const newLaneData = {
    envelope: {
      event_type_id: "2056",
      event_type_version_id: "2",
      event_template_id: "126",
      event_template_version_id: "1"
    },
    clickstream_trigger: {
      trigger_type_nm: "interaction",
      trigger_location_nm: "cms",
      trigger_source_nm: "cms_form",
      trigger_object_nm: "no_trigger_object"
    },
    semantic: {
      semantic_event_nm: "cms_engagement"
    },
    cms_info: cmsInfo
  };
  event(newLaneData);
};
function AffordabilityFields({
  className
}) {
  const [advanced, setAdvanced] = useState(false);
  const form = useFormContext();
  form.register(FieldConfig.maximumPayment.name, FieldConfig.maximumPayment.config);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(true);
  }, []);
  if (!loaded) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsxs(CondtionalField, { name: FieldConfig.customPayment.name, value: false, children: [
      /* @__PURE__ */ jsx(TextField, { label: "Annual income", name: FieldConfig.annualIncome.name, leftAdornment: "$", config: FieldConfig.annualIncome.config, description: /* @__PURE__ */ jsx(ToggleCustomPayment, {}), autoComplete: "off", format: commaSeparateNumber, onBlur: () => {
        trackLabel("Annual Income Edit");
      }, onChange: () => handleFieldChange("Annual income"), tooltip: {
        title: "Annual income",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Your total annual earnings before taxes, for you and your co-borrower. Annual income includes your base salary and any additional commissions, bonuses, tips, and investment income." })
      } }),
      /* @__PURE__ */ jsx(TextField, { label: "Monthly debts", name: FieldConfig.monthlyDebts.name, leftAdornment: "$", config: FieldConfig.monthlyDebts.config, autoComplete: "off", format: commaSeparateNumber, onBlur: () => {
        trackLabel("Monthly Debts Edit");
      }, onChange: () => handleFieldChange("Monthly debts") })
    ] }),
    /* @__PURE__ */ jsx(CondtionalField, { name: FieldConfig.customPayment.name, value: true, children: /* @__PURE__ */ jsx(TextField, { label: "Maximum payment", leftAdornment: "$", description: /* @__PURE__ */ jsx(ToggleCustomPayment, {}), name: FieldConfig.monthlyPayment.name, config: FieldConfig.monthlyPayment.config, autoComplete: "off", onBlur: () => {
      trackLabel("Maximum Payment Edit");
    }, onChange: () => {
      form.setValue(FieldConfig.maximumPayment.name, form.getValues(FieldConfig.monthlyPayment.name) * 2, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    }, format: commaSeparateNumber }) }),
    /* @__PURE__ */ jsx(TextField, { label: "Down payment", name: FieldConfig.downPayment.name, leftAdornment: "$", config: FieldConfig.downPayment.config, autoComplete: "off", format: commaSeparateNumber, onBlur: () => {
      trackLabel("Down Payment Edit");
    }, onChange: () => handleFieldChange("Down payment") }),
    /* @__PURE__ */ jsx(Box, { css: {
      marginBottom: "xs"
    }, children: /* @__PURE__ */ jsx(TextButton, { type: "button", onClick: yieldCallback(() => {
      setAdvanced(!advanced);
      if (advanced) {
        trackLabel("Show Simple Calc");
      } else {
        trackLabel("Show Advanced Calc");
      }
    }), children: advanced ? "Simple" : "Advanced" }) }),
    advanced && /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsx(CondtionalField, { name: FieldConfig.customPayment.name, value: false, children: /* @__PURE__ */ jsx(TextField, { label: "Debt-to-income", name: FieldConfig.dti.name, rightAdornment: "%", config: FieldConfig.dti.config, autoComplete: "off", tooltip: {
        title: "Debt-to-income (DTI) ratio",
        body: /* @__PURE__ */ jsxs(React.Fragment, { children: [
          /* @__PURE__ */ jsx(Paragraph, { children: "Enter the percentage of your income you are comfortable spending towards your mortgage. This number will also take monthly debts into account." }),
          /* @__PURE__ */ jsx(Paragraph, { children: "Typically your DTI should be 36% or lower to qualify for a mortgage. Certain loans allow for higher DTIs, such as FHA loans (43%) and VA or USDA loans (41%)." })
        ] })
      }, onBlur: () => {
        trackLabel("Debt-to-income Edit");
      }, onChange: () => handleFieldChange("Debt-to-income") }) }),
      /* @__PURE__ */ jsx(TextField, { label: "Interest Rate", name: FieldConfig.rate.name, rightAdornment: "%", config: FieldConfig.rate.config, autoComplete: "off", tooltip: {
        title: "Interest Rate",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. Close" })
      }, onBlur: () => {
        trackLabel("Rate Edit");
      }, onChange: () => handleFieldChange("Interest rate") }),
      /* @__PURE__ */ jsx(TextField, { label: "Loan term", name: FieldConfig.term.name, rightAdornment: "mos", config: FieldConfig.term.config, autoComplete: "off", tooltip: {
        title: "Loan term",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "The loan term is the amount of time set by the lender for a buyer to pay off a mortgage. To convert a loan term to months from years, multiply the number of years by 12." })
      }, onBlur: () => {
        trackLabel("Term Edit");
      }, onChange: () => handleFieldChange("Loan term") }),
      /* @__PURE__ */ jsx(CheckboxField, { label: "Include PMI", name: FieldConfig.includePMI.name, tooltip: {
        title: "Private mortgage insurance",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Mortgage insurance is required primarily for borrowers on a conventional loan with a down payment of less than 20% of the home's purchase price. It protects lenders against some or most of the losses that can occur when a borrower defaults on a mortgage loan. Contact your lender for more details." })
      }, onChange: (e) => {
        trackLabel("Include PMI Edit");
        if (e.target.checked) {
          trackLabel("Include PMI Edit checked");
        } else {
          trackLabel("Include PMI Edit unchecked");
        }
      } }),
      /* @__PURE__ */ jsx(CheckboxField, { label: "Include taxes/insurance", name: FieldConfig.includeTaxesInsurance.name, tooltip: {
        title: "Taxes and insurance",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues." })
      }, onChange: (e) => {
        trackLabel("Include Taxes/Insurance Edit");
        if (e.target.checked) {
          trackLabel("Include Taxes/Insurance Edit checked");
        } else {
          trackLabel("Include Taxes/Insurance Edit unchecked");
        }
      } }),
      /* @__PURE__ */ jsxs(CondtionalField, { name: FieldConfig.includeTaxesInsurance.name, value: true, children: [
        /* @__PURE__ */ jsx(TextField, { label: "Property tax", name: FieldConfig.propertyTaxRate.name, rightAdornment: "%", config: FieldConfig.propertyTaxRate.config, autoComplete: "off", tooltip: {
          title: "Property tax",
          body: /* @__PURE__ */ jsx(Paragraph, { children: "The mortgage payment calculator includes estimated property taxes. The value represents an annual tax on homeowner's property and the tax amount is based on the home's value." })
        }, onBlur: () => {
          trackLabel("Annual Property Tax Rate Edit");
        }, onChange: () => handleFieldChange("Property tax") }),
        /* @__PURE__ */ jsx(TextField, { label: "Home insurance", name: FieldConfig.annualHomeownersInsurance.name, leftAdornment: "$", rightAdornment: "/yr", config: FieldConfig.annualHomeownersInsurance.config, format: commaSeparateNumber, autoComplete: "off", tooltip: {
          title: "Home insurance",
          body: /* @__PURE__ */ jsx(Paragraph, { children: "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance. Close" })
        }, onBlur: () => {
          trackLabel("Annual Homeowners Insurance Edit");
        }, onChange: () => handleFieldChange("Home insurance") })
      ] }),
      /* @__PURE__ */ jsx(TextField, { label: "HOA dues", name: FieldConfig.monthlyHOA.name, leftAdornment: "$", rightAdornment: "/mo", config: FieldConfig.monthlyHOA.config, autoComplete: "off", format: commaSeparateNumber, tooltip: {
        title: "Homeowners association dues",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance." })
      }, onBlur: () => {
        trackLabel("Monthly HOA Edit");
      }, onChange: () => handleFieldChange("HOA dues") })
    ] }),
    /* @__PURE__ */ jsx(LegalPopover, { triggerText: "Calculator disclaimer", text: "The affordability calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend." })
  ] });
}
function ToggleCustomPayment() {
  const form = useFormContext();
  const customPayment = useWatch({
    name: FieldConfig.customPayment.name
  });
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    "Calculate by",
    " ",
    /* @__PURE__ */ jsx(TextButton, { type: "button", textStyle: "body-sm", onClick: yieldCallback(() => {
      if (customPayment) {
        trackLabel("Calculate Method Click Income");
      } else {
        trackLabel("Calculate Method Click Payment");
      }
      form.setValue(FieldConfig.customPayment.name, !customPayment);
    }), tabIndex: -1, children: customPayment ? "income" : "payment" })
  ] });
}
export {
  AffordabilityFields as default
};
